import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function todayStr() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function monthFirstStr() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function weekRangeStr() {
  const d = new Date();
  const day = d.getDay(); // 0 sun
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMon);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [monday.toISOString().slice(0, 10), sunday.toISOString().slice(0, 10)];
}
function fmtDateTime(s) {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s || '';
  }
}

export default function UserPointsLedger() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // top filters
  const [search, setSearch] = useState('');
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [addedBy, setAddedBy] = useState('');
  const [minPoints, setMinPoints] = useState('');
  const [maxPoints, setMaxPoints] = useState('');
  const [preset, setPreset] = useState('thisMonth');
  const [dateFrom, setDateFrom] = useState(monthFirstStr());
  const [dateTo, setDateTo] = useState(todayStr());

  // header filters for visible columns
  const [colUsername, setColUsername] = useState('');
  const [colType, setColType] = useState('');
  const [colStatus, setColStatus] = useState('');
  const [colMinPoints, setColMinPoints] = useState('');
  const [colDateFrom, setColDateFrom] = useState('');
  const [colDateTo, setColDateTo] = useState('');

  // sorting (visible columns only)
  const [sortBy, setSortBy] = useState('TRANSACTION_DATE');
  const [sortOrder, setSortOrder] = useState('DESC');

  const token = localStorage.getItem('token');

  function applyPreset(p) {
    setPreset(p);
    if (p === 'today') {
      const t = todayStr();
      setDateFrom(t); setDateTo(t);
    } else if (p === 'thisWeek') {
      const [f, t] = weekRangeStr();
      setDateFrom(f); setDateTo(t);
    } else if (p === 'thisMonth') {
      setDateFrom(monthFirstStr()); setDateTo(todayStr());
    }
  }

  const buildParams = (overrides = {}) => {
    const params = new URLSearchParams();
    const _page = overrides.page ?? page;
    const _limit = overrides.limit ?? limit;

    params.set('page', _page);
    params.set('limit', _limit);

    if (search) params.set('search', search.trim());
    if (username) params.set('username', username.trim());
    if (userId) params.set('userId', userId.trim());
    if (type) params.set('type', type);
    if (status) params.set('status', status);
    if (addedBy) params.set('addedBy', addedBy.trim());
    if (minPoints) params.set('minPoints', minPoints);
    if (maxPoints) params.set('maxPoints', maxPoints);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);

    // header filters override base
    if (colUsername) params.set('username', colUsername.trim());
    if (colType) params.set('type', colType);
    if (colStatus) params.set('status', colStatus);
    if (colMinPoints) params.set('minPoints', colMinPoints.trim());
    if (colDateFrom) params.set('dateFrom', colDateFrom);
    if (colDateTo) params.set('dateTo', colDateTo);

    params.set('sortBy', sortBy);
    params.set('sortOrder', sortOrder);

    return params;
  };

  async function fetchData() {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/company/user-points-ledger?${buildParams().toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('Fetch user-points-ledger failed:', err);
      alert('Failed to fetch points ledger');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, limit, sortBy, sortOrder]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [
    search, username, userId, type, status, addedBy, minPoints, maxPoints,
    dateFrom, dateTo, colUsername, colType, colStatus, colMinPoints, colDateFrom, colDateTo
  ]);

  const changeSort = (col) => {
    if (sortBy === col) setSortOrder(o => (o === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(col); setSortOrder('ASC'); }
  };

  // ---------- Excel export ----------
  const exportLedgerToExcel = (data, filename) => {
    // Export only the visible columns
    const exportRows = data.map((r) => ({
      Username: r.username || '',
      POINTS: r.POINTS,
      TRANSACTION_DATE: fmtDateTime(r.TRANSACTION_DATE),
      VALID_TILL_DATE: r.VALID_TILL_DATE || '',
      TYPE: r.TYPE,
      USED_POINTS: r.USED_POINTS,
      STATUS: r.STATUS || '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'UserPointsLedger');
    XLSX.writeFile(wb, filename);
  };

  const handleExportPage = () => {
    exportLedgerToExcel(rows, `user-points-ledger_page-${page}.xlsx`);
  };

  const handleExportAll = async () => {
    try {
      // fetch page 1 to know totalPages if needed
      const paramsFirst = buildParams({ page: 1 });
      const firstRes = await axios.get(
        `${BACKEND_URL}/api/company/user-points-ledger?${paramsFirst.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const all = [...(firstRes.data.rows || [])];
      const pages = firstRes.data.totalPages || 1;

      for (let p = 2; p <= pages; p++) {
        const paramsLoop = buildParams({ page: p });
        const res = await axios.get(
          `${BACKEND_URL}/api/company/user-points-ledger?${paramsLoop.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        all.push(...(res.data.rows || []));
      }

      exportLedgerToExcel(all, `user-points-ledger_all_${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch (err) {
      console.error('Export all ledger failed:', err);
      alert('Failed to export all rows');
    }
  };

  // visible columns: Username | POINTS | TRANSACTION_DATE | VALID_TILL_DATE | TYPE | USED_POINTS | STATUS
  const COLSPAN = 7;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">User Points Ledger</h1>
        <div className="flex gap-2">
          <button onClick={handleExportPage} className="px-3 py-1 border rounded">
            Export Page (Excel)
          </button>
          <button onClick={handleExportAll} className="px-3 py-1 border rounded">
            Export All (Excel)
          </button>
        </div>
      </div>

      {/* Top Filters */}
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div className="flex flex-col">
          <label className="text-sm">Common Search</label>
          <input
            className="border px-2 py-1 rounded"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="username / name / email"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Username</label>
          <input
            className="border px-2 py-1 rounded"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">User ID</label>
          <input
            className="border px-2 py-1 rounded"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Type</label>
          <select
            className="border px-2 py-1 rounded"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">All</option>
            <option value="CREDIT">CREDIT</option>
            <option value="DEBIT">DEBIT</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Status</label>
          <select
            className="border px-2 py-1 rounded"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="EXPIRED">EXPIRED</option>
            <option value="CONSUMED">CONSUMED</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Added By (User ID)</label>
          <input
            className="border px-2 py-1 rounded"
            value={addedBy}
            onChange={(e) => setAddedBy(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Min Points</label>
          <input
            type="number"
            className="border px-2 py-1 rounded"
            value={minPoints}
            onChange={(e) => setMinPoints(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Max Points</label>
          <input
            type="number"
            className="border px-2 py-1 rounded"
            value={maxPoints}
            onChange={(e) => setMaxPoints(e.target.value)}
          />
        </div>
      </div>

      {/* Date presets */}
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 border rounded ${preset === 'today' ? 'bg-gray-200' : ''}`}
            onClick={() => applyPreset('today')}
          >
            Today
          </button>
          <button
            className={`px-3 py-1 border rounded ${preset === 'thisWeek' ? 'bg-gray-200' : ''}`}
            onClick={() => applyPreset('thisWeek')}
          >
            This Week
          </button>
          <button
            className={`px-3 py-1 border rounded ${preset === 'thisMonth' ? 'bg-gray-200' : ''}`}
            onClick={() => applyPreset('thisMonth')}
          >
            This Month
          </button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">From</label>
          <input
            type="date"
            className="border px-2 py-1 rounded"
            value={dateFrom}
            onChange={(e) => { setPreset('custom'); setDateFrom(e.target.value); }}
          />
          <label className="text-sm">To</label>
          <input
            type="date"
            className="border px-2 py-1 rounded"
            value={dateTo}
            onChange={(e) => { setPreset('custom'); setDateTo(e.target.value); }}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Page</label>
          <input
            type="number"
            value={page}
            onChange={(e) => setPage(Math.max(1, parseInt(e.target.value || '1', 10)))}
            className="border px-2 py-1 rounded w-20"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Limit</label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="border px-2 py-1 rounded"
          >
            {[10, 25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="overflow-auto rounded">
        <table className="min-w-full border border-gray-300 border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-left">
                Username
              </th>
              <th
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer"
                onClick={() => changeSort('POINTS')}
              >
                POINTS
              </th>
              <th
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-center cursor-pointer"
                onClick={() => changeSort('TRANSACTION_DATE')}
              >
                TRANSACTION_DATE
              </th>
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-center">
                VALID_TILL_DATE
              </th>
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-center">
                TYPE
              </th>
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-right">
                USED_POINTS
              </th>
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-center">
                STATUS
              </th>
            </tr>

            {/* header filters for visible columns */}
            <tr className="bg-white">
              <th className="border border-gray-200 p-1">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  placeholder="Username"
                  value={colUsername}
                  onChange={(e) => setColUsername(e.target.value)}
                />
              </th>
              <th className="border border-gray-200 p-1">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1 text-right"
                  placeholder=">= Points"
                  value={colMinPoints}
                  onChange={(e) => setColMinPoints(e.target.value)}
                />
              </th>
              <th className="border border-gray-200 p-1">
                <div className="flex gap-1">
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded px-2 py-1"
                    value={colDateFrom}
                    onChange={(e) => setColDateFrom(e.target.value)}
                  />
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded px-2 py-1"
                    value={colDateTo}
                    onChange={(e) => setColDateTo(e.target.value)}
                  />
                </div>
              </th>
              <th className="border border-gray-200 p-1"></th>
              <th className="border border-gray-200 p-1">
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  value={colType}
                  onChange={(e) => setColType(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="CREDIT">CREDIT</option>
                  <option value="DEBIT">DEBIT</option>
                </select>
              </th>
              <th className="border border-gray-200 p-1"></th>
              <th className="border border-gray-200 p-1">
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  value={colStatus}
                  onChange={(e) => setColStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="EXPIRED">EXPIRED</option>
                  <option value="CONSUMED">CONSUMED</option>
                </select>
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td className="p-3 text-center border border-gray-200" colSpan={COLSPAN}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="p-3 text-center border border-gray-200" colSpan={COLSPAN}>
                  No data
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={`${idx}-${r.TRANSACTION_DATE}`} className="odd:bg-white even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2 text-left align-middle">
                    {r.username || '-'}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-right align-middle">
                    {r.POINTS}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-center align-middle whitespace-nowrap">
                    {fmtDateTime(r.TRANSACTION_DATE)}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-center align-middle">
                    {r.VALID_TILL_DATE || '-'}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-center align-middle">
                    {r.TYPE}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-right align-middle">
                    {r.USED_POINTS}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-center align-middle">
                    {r.STATUS || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div> Total: {total} • Pages: {totalPages} </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 border rounded"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <button
            className="px-3 py-1 border rounded"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
